import { useState, useCallback, useEffect, useMemo } from 'react';
import { Button, Box, CircularProgress, Typography, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { CheckCircle as CheckCircleIcon, Error as ErrorIcon, Warning as WarningIcon } from '@mui/icons-material';
import { fetchWithAuth } from '../../api/backendApi';
import flowsApi from '../../services/flowsApiService';
import { isFailedExecutionResult } from '../../utils/executionStatusDisplay';
import RtpPagingDialog from '../../pages/HomeLauncher/components/RtpPagingDialog';
import SipPagingDialog from '../../pages/HomeLauncher/components/SipPagingDialog';

// Incident type action configurations
const INCIDENT_ACTION_CONFIGS = {
  lockdown: [
    {
      label: 'Initiate SIP Paging',
      icon: '📢',
      actionType: 'sip_paging',
      endpoint: '/sip-paging/authorize',
      requiresConfirmation: true,
      cooldownSeconds: 30,
      visibleRoles: ['admin', 'superadmin', 'tenant_admin', 'responder', 'overseer']
    },
    {
      label: 'Send SMS Alert',
      icon: '📱',
      actionType: 'sms_alert',
      endpoint: '/notifications/sms',
      requiresConfirmation: true,
      cooldownSeconds: 60,
      visibleRoles: ['admin', 'superadmin', 'tenant_admin', 'responder', 'overseer']
    },
    {
      label: 'Display Alert Image',
      icon: '🖼️',
      actionType: 'image_display',
      endpoint: '/media/image-display',
      requiresConfirmation: false,
      cooldownSeconds: 10,
      visibleRoles: ['admin', 'superadmin', 'tenant_admin', 'responder', 'overseer']
    }
  ],
  medical: [
    {
      label: 'Call 911',
      icon: '🚑',
      actionType: 'call_911',
      endpoint: '/sip-paging/authorize',
      requiresConfirmation: true,
      cooldownSeconds: 60,
      visibleRoles: ['admin', 'superadmin', 'tenant_admin', 'responder', 'overseer']
    },
    {
      label: 'Notify Medical Staff',
      icon: '📱',
      actionType: 'notify_medical',
      endpoint: '/notifications/sms',
      requiresConfirmation: false,
      cooldownSeconds: 30,
      visibleRoles: ['admin', 'superadmin', 'tenant_admin', 'responder', 'overseer']
    }
  ],
  fire: [
    {
      label: 'Activate Fire Alarm',
      icon: '🔥',
      actionType: 'fire_alarm',
      endpoint: '/sip-paging/authorize',
      requiresConfirmation: true,
      cooldownSeconds: 30,
      visibleRoles: ['admin', 'superadmin', 'tenant_admin', 'responder', 'overseer']
    },
    {
      label: 'Call Fire Department',
      icon: '🚒',
      actionType: 'call_fire',
      endpoint: '/sip-paging/authorize',
      requiresConfirmation: true,
      cooldownSeconds: 60,
      visibleRoles: ['admin', 'superadmin', 'tenant_admin', 'responder', 'overseer']
    }
  ],
  panic_portable: [
    {
      label: 'Dispatch Security',
      icon: '🚨',
      actionType: 'dispatch_security',
      endpoint: '/notifications/sms',
      requiresConfirmation: false,
      cooldownSeconds: 10,
      visibleRoles: ['admin', 'superadmin', 'tenant_admin', 'responder', 'overseer']
    },
    {
      label: 'Initiate Paging',
      icon: '📢',
      actionType: 'sip_paging',
      endpoint: '/sip-paging/authorize',
      requiresConfirmation: true,
      cooldownSeconds: 30,
      visibleRoles: ['admin', 'superadmin', 'tenant_admin', 'responder', 'overseer']
    }
  ],
  weather: [
    {
      label: 'Send Weather Alert',
      icon: '⛈️',
      actionType: 'weather_alert',
      endpoint: '/notifications/sms',
      requiresConfirmation: false,
      cooldownSeconds: 30,
      visibleRoles: ['admin', 'superadmin', 'tenant_admin', 'responder', 'overseer']
    }
  ],
  security: [
    {
      label: 'Dispatch Security',
      icon: '🚨',
      actionType: 'dispatch_security',
      endpoint: '/notifications/sms',
      requiresConfirmation: false,
      cooldownSeconds: 10,
      visibleRoles: ['admin', 'superadmin', 'tenant_admin', 'responder', 'overseer']
    }
  ],
  evacuation: [
    {
      label: 'Initiate Evacuation Alarm',
      icon: '🚪',
      actionType: 'evacuation_alarm',
      endpoint: '/sip-paging/authorize',
      requiresConfirmation: true,
      cooldownSeconds: 30,
      visibleRoles: ['admin', 'superadmin', 'tenant_admin', 'responder', 'overseer']
    }
  ],
  other: [
    {
      label: 'Send Notification',
      icon: '📱',
      actionType: 'notification',
      endpoint: '/notifications/sms',
      requiresConfirmation: false,
      cooldownSeconds: 30,
      visibleRoles: ['admin', 'superadmin', 'tenant_admin', 'responder', 'overseer']
    }
  ]
};

const ActionButton = ({ action, incident, userRoles, onActionComplete, onOpenRtpPaging, onOpenSipPaging }) => {
  const [executing, setExecuting] = useState(false);
  const [cooldownRemaining, setCooldownRemaining] = useState(0);
  const [status, setStatus] = useState(null); // 'success' | 'error' | null
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Check if user has required role
  const visibleRoles = Array.isArray(action?.visibleRoles) ? action.visibleRoles : [];
  const hasRole = visibleRoles.length === 0 || visibleRoles.some(role => userRoles.includes(role));
  if (!hasRole) return null;

  const handleClick = () => {
    if (cooldownRemaining > 0 || executing) return;

    if (action.requiresConfirmation) {
      setConfirmOpen(true);
    } else {
      executeAction();
    }
  };

  const executeAction = async () => {
    setConfirmOpen(false);
    setExecuting(true);
    setStatus(null);
    setErrorMessage('');

    try {
      const actionType = String(action?.actionType || '').trim();
      const idempotencyKey = `incident-${incident.incidentId}-${actionType}-${Date.now()}`;

      const resolveCanonicalEndpoint = () => {
        const tenantId = incident?.tenantId;
        if (!tenantId) return null;
        if (actionType === 'SEND_SMS') return `/api/v2/tenants/${tenantId}/notifications/sms`;
        if (actionType === 'IMAGE_DISPLAY') return `/api/v2/tenants/${tenantId}/media/image-display`;
        return null;
      };

      const maybeAppendActionRequested = async (payload) => {
        try {
          await fetchWithAuth(
            `/api/v2/tenants/${incident.tenantId}/incidents/${incident.incidentId}/events`,
            'POST',
            {
              type: 'ACTION_REQUESTED',
              payload,
            },
            { suppressErrorLog: true }
          );
        } catch (_) {
          // best-effort
        }
      };

      if (actionType === 'OPEN_URL') {
        const url = String(action.url || '').trim();
        if (!url) throw new Error('Missing URL');
        window.open(url, '_blank', 'noopener,noreferrer');
        await maybeAppendActionRequested({ actionType, actionLabel: action.label, url });
      } else if (actionType === 'TRIGGER_FLOW') {
        const flowId = String(action.flowId || '').trim();
        if (!flowId) throw new Error('Missing flowId');
        const result = await flowsApi.execute({ flowId, tenantId: incident.tenantId, source: 'incident', trigger: 'incident_action' });
        if (isFailedExecutionResult(result)) {
          throw new Error(result.message || result.error || 'Flow failed');
        }
        await maybeAppendActionRequested({ actionType, actionLabel: action.label, flowId });
      } else if (actionType === 'APPEND_EVENT') {
        const eventType = String(action.eventType || '').trim();
        if (!eventType) throw new Error('Missing eventType');
        await fetchWithAuth(
          `/api/v2/tenants/${incident.tenantId}/incidents/${incident.incidentId}/events`,
          'POST',
          {
            type: eventType,
            payload: {
              source: 'panel_button',
              actionLabel: action.label,
              idempotencyKey,
            },
          }
        );
      } else if (['SEND_SMS', 'IMAGE_DISPLAY'].includes(actionType)) {
        const canonical = resolveCanonicalEndpoint();
        if (!canonical) throw new Error('Missing canonical endpoint');

        await fetchWithAuth(
          canonical,
          'POST',
          {
            incidentId: incident.incidentId,
            actionType,
            idempotencyKey,
          }
        );

        await maybeAppendActionRequested({ actionType, actionLabel: action.label, endpoint: canonical });
      } else if (actionType === 'RTP_PAGING') {
        const zoneId = String(action?.zoneId || '').trim();
        if (!zoneId) throw new Error('Missing zoneId');
        if (typeof onOpenRtpPaging === 'function') {
          const zoneName = String(action?.zoneName || action?.label || zoneId).trim() || zoneId;
          onOpenRtpPaging({ zoneId, zoneName });
        }
        await maybeAppendActionRequested({ actionType, actionLabel: action.label, zoneId });
      } else if (actionType === 'SIP_PAGING' || actionType === 'SIP_CALL') {
        const zoneId = String(action?.zoneId || '').trim();
        if (!zoneId) throw new Error('Missing zoneId');
        if (typeof onOpenSipPaging === 'function') {
          const zoneName = String(action?.zoneName || action?.label || zoneId).trim() || zoneId;
          onOpenSipPaging({ zoneId, zoneName });
        }
        await maybeAppendActionRequested({ actionType, actionLabel: action.label, zoneId });
      } else {
        throw new Error(`Unsupported action type: ${actionType || '(missing)'}`);
      }

      setStatus('success');
      
      // Start cooldown
      if (action.cooldownSeconds) {
        setCooldownRemaining(action.cooldownSeconds);
        const interval = setInterval(() => {
          setCooldownRemaining(prev => {
            if (prev <= 1) {
              clearInterval(interval);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      }

      if (onActionComplete) {
        onActionComplete(action, 'success');
      }

      // Clear success status after 3 seconds
      setTimeout(() => setStatus(null), 3000);
    } catch (error) {
      console.error('[INCIDENT] Action failed:', error);
      setStatus('error');
      setErrorMessage(error.response?.data?.error || error.message || 'Action failed');
      
      if (onActionComplete) {
        onActionComplete(action, 'error', error);
      }

      // Clear error status after 5 seconds
      setTimeout(() => {
        setStatus(null);
        setErrorMessage('');
      }, 5000);
    } finally {
      setExecuting(false);
    }
  };

  const isDisabled = executing || cooldownRemaining > 0;

  return (
    <>
      <Button
        variant="contained"
        fullWidth
        onClick={handleClick}
        disabled={isDisabled}
        startIcon={
          executing ? (
            <CircularProgress size={20} color="inherit" />
          ) : status === 'success' ? (
            <CheckCircleIcon />
          ) : status === 'error' ? (
            <ErrorIcon />
          ) : (
            <span style={{ fontSize: '1.2rem' }}>{action.icon}</span>
          )
        }
        sx={{
          mb: 1.5,
          py: 1.5,
          justifyContent: 'flex-start',
          textAlign: 'left',
          bgcolor: status === 'success' ? 'success.main' : status === 'error' ? 'error.main' : 'primary.main',
          '&:hover': {
            bgcolor: status === 'success' ? 'success.dark' : status === 'error' ? 'error.dark' : 'primary.dark'
          },
          '&.Mui-disabled': {
            bgcolor: 'action.disabledBackground',
            color: 'action.disabled'
          }
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            {action.label}
          </Typography>
          {cooldownRemaining > 0 && (
            <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
              Cooldown: {cooldownRemaining}s
            </Typography>
          )}
          {status === 'error' && errorMessage && (
            <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'error.light' }}>
              {errorMessage}
            </Typography>
          )}
        </Box>
      </Button>

      {/* Confirmation Dialog */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="warning" />
          Confirm Action
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to execute: <strong>{action.label}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This action will be logged and may trigger external systems.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button onClick={executeAction} variant="contained" color="primary">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const IncidentActions = ({ incident, userRoles, theme }) => {
  const [typeButtons, setTypeButtons] = useState([]);
  const [rtpDialogOpen, setRtpDialogOpen] = useState(false);
  const [sipDialogOpen, setSipDialogOpen] = useState(false);
  const [pagingPreset, setPagingPreset] = useState({ zoneId: null, zoneName: null });

  useEffect(() => {
    let active = true;

    const loadIncidentTypeButtons = async () => {
      const tenantId = incident?.tenantId;
      const typeKey = incident?.type;
      if (!tenantId || !typeKey) {
        if (active) setTypeButtons([]);
        return;
      }

      try {
        const resp = await fetchWithAuth(
          `/api/v2/tenants/${tenantId}/incident-types/${typeKey}`,
          'GET',
          null,
          { suppressErrorLog: true }
        );

        const it = resp?.incidentType || resp?.data?.incidentType;
        const buttons = Array.isArray(it?.buttons) ? it.buttons : [];
        if (active) setTypeButtons(buttons);
      } catch (_) {
        if (active) setTypeButtons([]);
      }
    };

    loadIncidentTypeButtons();
    return () => {
      active = false;
    };
  }, [incident?.tenantId, incident?.type]);

  const actions = useMemo(() => {
    if (Array.isArray(typeButtons) && typeButtons.length > 0) {
      return typeButtons.map((b) => {
        if (!b || typeof b !== 'object') return b;
        if (b.actionType === 'OPEN_URL') {
          return {
            ...b,
            url: String(b.url || '').trim(),
          };
        }
        return b;
      });
    }

    return INCIDENT_ACTION_CONFIGS[incident.type] || INCIDENT_ACTION_CONFIGS.other;
  }, [typeButtons, incident.type]);

  const handleActionComplete = useCallback((action, status, error) => {
    console.log('[INCIDENT] Action completed:', action.label, status);
  }, []);

  const handleOpenRtpPaging = useCallback(({ zoneId, zoneName }) => {
    setPagingPreset({ zoneId: zoneId || null, zoneName: zoneName || zoneId || null });
    setRtpDialogOpen(true);
  }, []);

  const handleOpenSipPaging = useCallback(({ zoneId, zoneName }) => {
    setPagingPreset({ zoneId: zoneId || null, zoneName: zoneName || zoneId || null });
    setSipDialogOpen(true);
  }, []);

  if (actions.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No actions configured for this incident type.
      </Typography>
    );
  }

  return (
    <Box>
      {actions.map((action, index) => (
        <ActionButton
          key={`${action.actionType}-${index}`}
          action={action}
          incident={incident}
          userRoles={userRoles}
          onActionComplete={handleActionComplete}
          onOpenRtpPaging={handleOpenRtpPaging}
          onOpenSipPaging={handleOpenSipPaging}
        />
      ))}

      <RtpPagingDialog
        open={rtpDialogOpen}
        onClose={() => {
          setRtpDialogOpen(false);
          setPagingPreset({ zoneId: null, zoneName: null });
        }}
        presetZoneId={pagingPreset.zoneId}
        presetZoneName={pagingPreset.zoneName}
        micDeviceId={null}
      />

      <SipPagingDialog
        open={sipDialogOpen}
        onClose={() => {
          setSipDialogOpen(false);
          setPagingPreset({ zoneId: null, zoneName: null });
        }}
        presetZoneId={pagingPreset.zoneId}
        presetZoneName={pagingPreset.zoneName}
        sipUri={null}
      />
    </Box>
  );
};

export default IncidentActions;
